import React from "react";
import { Button, Checkbox, Stack, Typography } from "@mui/material";

import { CoreNetworkMemberAvatar, PagedTable, SectionCard, StatusChip, formatCurrency, safeArray, toTitleCase } from "../coreNetworkUiShared";

export default function CoreNetworkMatchPanel({
  matches = [],
  selectedMatchIds = [],
  onToggleMatch,
  onGenerateMatches,
  onShortlistMatches,
  onSendOffers,
  busyAction = "",
  canViewPayment = false,
}) {
  const activeMatches = safeArray(matches);
  const columns = [
    {
      key: "select",
      label: "",
      minWidth: 60,
      render: (row) => (
        <Checkbox
          size="small"
          checked={selectedMatchIds.includes(row.id)}
          onChange={() => onToggleMatch?.(row.id)}
        />
      ),
    },
    {
      key: "member",
      label: "Member",
      render: (row) => (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CoreNetworkMemberAvatar member={row.member} size={34} />
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.member?.displayName || "Unknown member"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.member?.companyName || toTitleCase(row.membership?.role)}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
    {
      key: "overallScore",
      label: "Organic",
      render: (row) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.overallScore || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Organic score
          </Typography>
        </Stack>
      ),
    },
    { key: "capabilityScore", label: "Capability", render: (row) => row.capabilityScore || "-" },
    { key: "capacityScore", label: "Capacity", render: (row) => row.capacityScore || "-" },
    { key: "ratingScore", label: "Rating", render: (row) => row.ratingScore || "-" },
    {
      key: "placement",
      label: "Placement",
      render: (row) => (
        <Stack spacing={0.5}>
          {row.isSponsored ? <StatusChip value="sponsored" /> : <Typography variant="body2">Organic</Typography>}
          <Typography variant="caption" color="text.secondary">
            {row.isSponsored ? (row.sponsoredDisclosure || "Sponsored placement") : `Boost: ${row.sponsoredScore || 0}`}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "breakdown",
      label: "Breakdown",
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          Cap {row.capabilityScore || 0} | Capacity {row.capacityScore || 0} | Rating {row.ratingScore || 0} | Tier {row.tierScore || 0} | Sponsored {row.sponsoredScore || 0}
        </Typography>
      ),
    },
    {
      key: "reason",
      label: "Why matched",
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.matchReasonJson?.capability || row.matchReasonJson?.tier || "Scored from capability, capacity, logistics, rating, price, and tier inputs."}
        </Typography>
      ),
    },
    ...(canViewPayment
      ? [{ key: "estimatedCost", label: "Est. cost", render: (row) => formatCurrency(row.estimatedCost, "CAD") }]
      : []),
  ];

  return (
    <SectionCard
      title="Matches"
      subtitle="Scored recommendations for this request. Sponsored placement is shown separately from organic scoring."
      action={
        <Stack direction="row" spacing={1}>
          {onGenerateMatches ? (
            <Button variant="outlined" onClick={onGenerateMatches} disabled={busyAction === "generate"}>
              {busyAction === "generate" ? "Generating..." : "Generate"}
            </Button>
          ) : null}
          {onShortlistMatches ? (
            <Button variant="outlined" onClick={onShortlistMatches} disabled={!selectedMatchIds.length || busyAction === "shortlist"}>
              {busyAction === "shortlist" ? "Saving..." : "Shortlist"}
            </Button>
          ) : null}
          {onSendOffers ? (
            <Button variant="contained" onClick={onSendOffers} disabled={busyAction === "offers"}>
              {busyAction === "offers" ? "Sending..." : "Send offers"}
            </Button>
          ) : null}
        </Stack>
      }
    >
      <PagedTable
        rows={activeMatches}
        columns={columns}
        emptyTitle="No matches yet"
        emptyDescription="Generate matches to score active members for this work request."
      />
    </SectionCard>
  );
}
